import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoadingScreen } from '@puzzlemania/lobby';
import type { PluginLoader } from '@puzzlemania/plugins';
import type { RemoteConfigService } from '@puzzlemania/remote-config';
import { usePlatformServices } from '../composition/PlatformServicesContext';
import { ErrorBoundary } from '../observability/ErrorBoundary';
import { GameErrorFallback } from '../observability/GameErrorFallback';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const DESTROY_DELAY_MS = 500;
// WP-VIS.11 — matches --duration-slow (global.css), the CSS transition applied
// to .screen--game--exiting.
const FADE_OUT_MS = 400;

export function GameScreen({
  loader: injectedLoader,
  rc: injectedRc,
}: { loader?: PluginLoader; rc?: RemoteConfigService } = {}) {
  const { id: gameId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const platform = usePlatformServices();
  const loaderRef = useRef(injectedLoader ?? platform.pluginLoader);
  const rcRef = useRef(injectedRc ?? platform.remoteConfig);
  const containerRef = useRef<HTMLDivElement>(null);
  const destroyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useDocumentMeta(
    gameId ? t('meta.game.title', { name: t(`game.${gameId}.name`) }) : t('app.name'),
    gameId ? t(`game.${gameId}.desc`) : undefined,
  );

  // Keep stable refs so the plugin effect never re-runs due to navigate/t identity changes.
  const navigateRef = useRef(navigate);
  const tRef = useRef(t);
  useEffect(() => {
    navigateRef.current = navigate;
    tRef.current = t;
  });

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    // Cancel any pending destroy from a previous cleanup (React Strict Mode double-mount).
    if (destroyTimerRef.current !== null) {
      clearTimeout(destroyTimerRef.current);
      destroyTimerRef.current = null;
    }
    setIsExiting(false);

    const loader = loaderRef.current;
    const rc = rcRef.current;
    const container = containerRef.current;
    if (!container || !gameId) return;
    let alive = true;

    if (!rc.get(`${gameId}_enabled`, true)) {
      navigateRef.current('/', { state: { toast: tRef.current('game.disabled') } });
      return;
    }

    // WP-VIS.11 — the game's own HUD "Home" button calls this (via
    // IGameContext.requestExit) instead of the prior direct window.history.back(),
    // which unmounted the game synchronously in the same commit that mounted the
    // Lobby (the actual cause of the two screens briefly overlapping). This fades
    // the game out, properly awaits the real Pixi teardown (not a race against the
    // natural cleanup's DESTROY_DELAY_MS timer below — that timer still exists for
    // every *other* unmount path, e.g. the browser back button or Strict Mode's
    // synthetic remount, which can't be intercepted before-the-fact like this can),
    // then navigates only once the game is actually gone.
    const requestExit = () => {
      setIsExiting(true);
      setTimeout(() => {
        loader.pauseActive();
        void loader.destroyActive().then(() => {
          navigateRef.current('/');
        });
      }, FADE_OUT_MS);
    };

    loader
      .activate(gameId, container, requestExit)
      .then(() => {
        if (alive) setIsLoading(false);
      })
      .catch(() => {
        navigateRef.current('/');
      });

    return () => {
      alive = false;
      const pausedPlugin = loader.pauseActive();
      destroyTimerRef.current = setTimeout(() => {
        destroyTimerRef.current = null;
        // destroyIfStillActive, not destroyActive: if the player re-entered this
        // same game before this timer fired, activate() already replaced the
        // loader's active plugin with a fresh instance — this stale timer must
        // not tear that fresh instance down.
        void loader.destroyIfStillActive(pausedPlugin);
      }, DESTROY_DELAY_MS);
    };
    // Only re-run when the game changes. navigate and t are accessed via refs.
  }, [gameId]);

  return (
    <ErrorBoundary
      logger={platform.logger}
      fallback={<GameErrorFallback onReturnToLobby={handleBack} />}
    >
      <div
        className={`screen screen--game${isExiting ? ' screen--game--exiting' : ''}`}
        data-testid="game-screen"
      >
        {/*
          game-container is kept empty from React's perspective so PixiJS can
          imperatively append its canvas without React's reconciler touching it.
          game-loading-overlay is a sibling (not a child) of game-container so
          React's deletion of the overlay never calls removeChild on game-container.
        */}
        <div ref={containerRef} className="game-container" />
        {isLoading && (
          <div className="game-loading-overlay">
            <LoadingScreen {...(gameId ? { gameId } : {})} />
          </div>
        )}
        {/* Accessible back button — visually hidden behind the PixiJS HUD which renders its own home button */}
        {!isLoading && (
          <button className="game-screen-back-btn" aria-label={t('game.back')} onClick={handleBack}>
            {t('game.back')}
          </button>
        )}
      </div>
    </ErrorBoundary>
  );
}
